import { ConfirmationModal } from '@shared/components';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useBeforeUnload, useBlocker, type BlockerFunction } from 'react-router-dom';

/**
 * LeavingListener component.
 *
 * Prevents accidental data loss by warning users before they leave the current page.
 * It shows a confirmation when the user navigates to a different route and uses
 * `useBeforeUnload` to warn them before closing the tab or reloading the page.
 */

interface LeavingListenerProps {
  shouldBlockNavigation?: boolean;
}

export default function LeavingListener(props: Readonly<LeavingListenerProps>) {
  const { shouldBlockNavigation = false } = props;
  // NOTE: This is just a workaround for blocker
  // Issue here: https://github.com/remix-run/react-router/issues/11430#issuecomment-2345829613
  // useRef to check if the component is mounted or not
  const isMountedRef = useRef<boolean>();
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const blockerFunction = useCallback<BlockerFunction>(
    ({ currentLocation, nextLocation }) =>
      !!isMountedRef.current &&
      currentLocation.pathname !== nextLocation.pathname &&
      shouldBlockNavigation,
    [shouldBlockNavigation],
  );

  const blocker = useBlocker(blockerFunction);

  const isBlocked = useMemo(
    () => blocker.state === 'blocked' && shouldBlockNavigation,
    [blocker, shouldBlockNavigation],
  );

  useBeforeUnload((event) => {
    // If the form is not dirty, we don't need to prevent the user from leaving.
    if (!shouldBlockNavigation) return;
    event.preventDefault();
  });

  const continueToNextPage = () => {
    blocker.proceed?.();
  };

  const cancelNavigation = () => {
    blocker.reset?.();
  };

  return (
    <ConfirmationModal
      open={isBlocked}
      title='ออกจากหน้านี้'
      content='คุณแน่ใจหรือว่าต้องการออกจากหน้านี้โดยไม่บันทึก'
      acceptText='ดำเนินการต่อในหน้านี้'
      discardText='ออกโดยไม่บันทึก'
      onDiscard={continueToNextPage}
      onClose={cancelNavigation}
      onAccept={cancelNavigation}
    />
  );
}
